import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { message } from 'antd';
import { Prompt } from 'components/Page';
import {
  add, clear, dirty, load, reset, update
} from 'actions';
import { isValidationBlockError } from 'actions/validationBlock';

/**
 * @description Map data and server flight
 * @param {state} Object
 * @param {owProps} Object
 */
const mapStateToProps = (state, ownProps) => {
  const extra = ownProps.reducer.extra ? {
    [ownProps.reducer.extra.key]: state.api[ownProps.reducer.extra.key].data.results || state.api[ownProps.reducer.extra.key].data
  } : {};

  return {
    item: ownProps.config.format ? ownProps.config.format({ ...state.api[ownProps.reducer.item.key].data, ...extra }) : { ...state.api[ownProps.reducer.item.key].data, ...extra },
    inFlight: state.api[ownProps.reducer.item.key].inFlight || (ownProps.reducer.extra ? state.api[ownProps.reducer.extra.key].inFlight : false),
    formError: state.api[ownProps.reducer.item.key].error
  };
};
/**
 * @description Dispatch create, loadList, load, deleteItem
 * @param {dispatch} Object
 */
const mapDispatchToProps = (dispatch, ownProps) => ({
  clearErrors() {
    dispatch(clear(ownProps.reducer.item.key));
  },
  resetItem() {
    dispatch(reset(ownProps.reducer.item.key));
    if (ownProps.reducer.extra) {
      dispatch(reset(ownProps.reducer.extra.key));
    }
  },
  loadItem(id) {
    return dispatch(load(ownProps.reducer.item.key, { id }))
      .then(() => (ownProps.onLoadItem ? ownProps.onLoadItem(id) : true));
  },
  reloadList() {
    dispatch(dirty(ownProps.reducer.list.key));
    if (ownProps.reducer.parent) {
      dispatch(dirty(ownProps.reducer.parent.key));
    }
  },
  saveItem(id, data) {
    return dispatch(id > 0 ? update(ownProps.reducer.item.key, { id, ...data }) : add(ownProps.reducer.item.key, data));
  }
});
/**
 * @description Launch the Prompt to Save or Discard changes
 * @param {Save} Function
 */
const promptSaveChanges = (save, cancel) => (
  Prompt({
    onOkClick: save,
    onCancelClick: cancel
  })
);
/**
 * LeaveTypes
 * @description Leave Type View
 * @version 1.0.0
 */
const Single = ({
  canAdmin,
  classes,
  clearErrors,
  config,
  formError,
  item,
  inFlight,
  loadItem,
  messaging,
  onPrepareData,
  onSuccessCallback,
  onFailureCallback,
  reloadList,
  saveItem,
  themeName
}) => {
  /*
   ** @description Hook into the lifecycle to get latest id
   */

  useEffect(() => {
    const id = config?.parent?.id;
    if (typeof id === 'number' && id > 0) {
      loadItem(id);
    }
  }, [config?.parent?.id]);

  // Is the form in a state of edit or not
  const isEditMode = (item.id !== null && item.id > 0) || false;
  /**
   * @description Save the item and dislpay a relevant message
   * @param {id} Number
   * @param {data} Object
   * @param {prevData} Object
   * @triggers {reloadList}
   */
  const handleSave = (id, data, prevData = null) => {
    const preparedData = onPrepareData(data);
    const postSuccessCallback = () => {
      message.success(messaging.save.success(data));
      if (prevData) {
        loadItem(prevData.id); // load previous data or reset the loaded item
      }
    };
    // null data aborts save, but still call callback
    if (preparedData === null) {
      return onSuccessCallback(null, data, item, postSuccessCallback);
    }
    return saveItem(id, preparedData)
      .then((response) => {
        onSuccessCallback(response, data, item, postSuccessCallback);
        reloadList(); // reload change list data
        return response;
      },
      (error) => {
        onFailureCallback(error, () => {
          // A blocked write (FR-5.1) is already surfaced by the global validation
          // dialog; suppress the redundant "save failed" toast for that case only.
          if (!isValidationBlockError(error)) {
            message.error(messaging.save.failure(data));
          }
        });
        return error;
      });
  };
  /**
   * @description Check if the form is dirty
   * @triggers {promptSaveChanges}
   */
  const handleFormClose = (currentItem, previousItem) => {
    // Prompt to save the changed form data
    promptSaveChanges(() => handleSave(previousItem.id, previousItem),
      () => {
      });
  };
  // Layout Component to build out the Page
  return (
    <div className={classNames(classes, 'bhp-container')}>
      {
        config.component({
          canAdmin,
          commands: {
            showCancelButton: false
          },
          dataSource: item,
          formError,
          inFlight,
          isEditMode,
          parent: config.parent,
          onSaveClick: handleSave,
          onFormClose: handleFormClose,
          onPopupOk: clearErrors,
          popupError: true,
          themeName,
          title: config.title
        })
      }
    </div>
  );
};

Single.propTypes = {
  canAdmin: PropTypes.bool,
  classes: PropTypes.string,
  clearErrors: PropTypes.func,
  config: PropTypes.object,
  formError: PropTypes.object,
  inFlight: PropTypes.bool,
  item: PropTypes.object,
  loadItem: PropTypes.func,
  onPrepareData: PropTypes.func,
  onSuccessCallback: PropTypes.func,
  onFailureCallback: PropTypes.func,
  messaging: PropTypes.object,
  saveItem: PropTypes.func,
  reloadList: PropTypes.func,
  themeName: PropTypes.string
};

Single.defaultProps = {
  canAdmin: false,
  classes: '',
  config: {},
  clearErrors() {
  },
  formError: {},
  inFlight: false,
  item: {},
  loadItem() {
  },
  messaging: {
    save: {
      failure: () => 'Saving failed',
      success: () => 'Saving was successful'
    }
  },
  onPrepareData(data) {
    return data;
  },
  onSuccessCallback(response, data, original, callback) {
    callback();
  },
  onFailureCallback(response, callback) {
    callback();
  },
  saveItem() {
  },
  reloadList() {
  },
  themeName: ''
};

export default connect(mapStateToProps, mapDispatchToProps)(Single);
